import { useEffect, useState } from 'react';
import BottomSheet from '../common/BottomSheet';
import ColorRow from '../common/ColorRow';
import DateInlineCalendar from './DateInlineCalendar';
import TimeWheelPicker, { HOURS, MINUTES } from './TimeWheelPicker';
import TemplatePickerRow from './TemplatePickerRow';
import { useApp } from '../../context/AppContext';
import { dateKey, parseDateKey } from '../../utils/dateUtils';

// 通知先（#notify=... タグ）。予定側にGoogleカレンダー標準の「通知」
// （リマインダー機能）は存在しない — 通知先の判定はLINE通知
// （Cloudflare cron-workerがこのタグを監視して送信）専用。
const NOTIFY_TARGET_OPTIONS = [
  { value: 'none', label: '通知しない' },
  { value: 'family', label: '家族全体' },
  { value: 'parents', label: '両親のみ' },
];

const DEFAULT_START_HOUR_INDEX = 9; // 09:00
const DEFAULT_END_HOUR_INDEX = 10; // 10:00

/** Index of the closest matching value in a sorted numeric-string array (used to snap an
 *  arbitrary minute value, e.g. from an event created elsewhere, onto the 5-minute wheel). */
function closestIndex(values, target) {
  const t = Number(target);
  let bestIdx = 0;
  let bestDiff = Infinity;
  values.forEach((v, i) => {
    const diff = Math.abs(Number(v) - t);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIdx = i;
    }
  });
  return bestIdx;
}

/**
 * "予定を追加" / "予定を編集" bottom sheet — a single form component
 * shared by both flows via `mode`, per spec ("新しい画面は作らず、追加と
 * 編集を同じフォームコンポーネントで実装"). Reads its open/mode/event
 * state directly from AppContext's `eventForm`:
 *   null                          -> closed
 *   { mode: 'add' }               -> add flow, prefilled from selectedDate
 *   { mode: 'edit', event }       -> edit flow, prefilled from that event
 *
 * Field order matches the finished mockup design exactly:
 * タイトル / 日付 / 終日・開始終了時間 / 通知先（#notify=... タグ）/
 * テンプレート / 場所 / 説明 / 色.
 *
 * ※Googleカレンダー標準の「通知」（リマインダー）機能は使用しない
 * （通知はCloudflare cron-workerが説明欄の#notify=...タグを監視して
 * LINEへ送信するため、Google Calendar API側のremindersは不要）。
 */
export default function EventFormSheet() {
  const { today, selectedDate, templates, eventForm, closeEventForm, addEvent, editEvent, showToast } =
    useApp();

  const open = eventForm != null;
  const mode = eventForm?.mode ?? 'add';
  const editingEvent = mode === 'edit' ? eventForm.event : null;

  const [title, setTitle] = useState('');
  const [date, setDate] = useState(selectedDate);
  const [allDay, setAllDay] = useState(false);
  const [startHourIdx, setStartHourIdx] = useState(DEFAULT_START_HOUR_INDEX);
  const [startMinuteIdx, setStartMinuteIdx] = useState(0);
  const [endHourIdx, setEndHourIdx] = useState(DEFAULT_END_HOUR_INDEX);
  const [endMinuteIdx, setEndMinuteIdx] = useState(0);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [notifyTarget, setNotifyTarget] = useState('none');
  const [templateId, setTemplateId] = useState(null);
  const [icon, setIcon] = useState('📅');
  const [color, setColor] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset/prefill the form every time the sheet opens: from the
  // tapped event when editing, or from today/selectedDate when adding.
  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && editingEvent) {
      const ev = editingEvent;
      setTitle(ev.title);
      setDate(parseDateKey(ev.date));
      setAllDay(ev.allDay);
      if (!ev.allDay && ev.start && ev.end) {
        const [sh, sm] = ev.start.split(':');
        const [eh, em] = ev.end.split(':');
        setStartHourIdx(closestIndex(HOURS, sh));
        setStartMinuteIdx(closestIndex(MINUTES, sm));
        setEndHourIdx(closestIndex(HOURS, eh));
        setEndMinuteIdx(closestIndex(MINUTES, em));
      } else {
        setStartHourIdx(DEFAULT_START_HOUR_INDEX);
        setStartMinuteIdx(0);
        setEndHourIdx(DEFAULT_END_HOUR_INDEX);
        setEndMinuteIdx(0);
      }
      setLocation(ev.location || '');
      // ev.description はAPI側で既に #notify=... を除去した本文のみ
      // （functions/_lib/notifyTag.js）なので、タグをユーザーに見せることはない。
      setDescription(ev.description || '');
      setNotifyTarget(ev.notifyTarget || 'none');
      setTemplateId(ev.templateId || null);
      setIcon(ev.icon || '📅');
      setColor(ev.color ?? null);
    } else {
      setTitle('');
      setDate(selectedDate);
      setAllDay(false);
      setStartHourIdx(DEFAULT_START_HOUR_INDEX);
      setStartMinuteIdx(0);
      setEndHourIdx(DEFAULT_END_HOUR_INDEX);
      setEndMinuteIdx(0);
      setLocation('');
      setDescription('');
      setNotifyTarget('none');
      setTemplateId(null);
      setIcon('📅');
      setColor(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, editingEvent]);

  const handleSelectTemplate = (id) => {
    setTemplateId(id);
    if (id === null) return;
    const t = templates.find((tpl) => tpl.id === id);
    if (!t) return;
    setTitle(t.name);
    setLocation(t.place);
    setColor(t.color);
    // テンプレートのicons配列（複数選択可）→ 予定側は現状まだ単一icon
    // フィールドのため、先頭のアイコンを反映する。予定自体のicons配列化
    // は「④今後の利用」の範囲（カレンダー・予定一覧・LINE通知と合わせて
    // 別途対応）。
    setIcon(t.icons?.[0] || '📅');
    showToast(`「${t.name}」の内容を反映しました`);
  };

  const handleSubmit = async () => {
    if (submitting) return; // extra guard against double-submit beyond the disabled button
    setSubmitting(true);
    try {
      const payload = {
        title: title.trim() || '(無題の予定)',
        date: dateKey(date),
        allDay,
        start: allDay ? null : `${HOURS[startHourIdx]}:${MINUTES[startMinuteIdx]}`,
        end: allDay ? null : `${HOURS[endHourIdx]}:${MINUTES[endMinuteIdx]}`,
        location,
        description,
        notifyTarget,
        icon,
        color: color ?? '#3F6659',
        templateId,
      };

      if (mode === 'edit') {
        await editEvent(editingEvent.id, payload);
        closeEventForm();
        showToast('予定を更新しました');
      } else {
        await addEvent(payload);
        closeEventForm();
        showToast('予定を追加しました');
      }
      // editEvent()/addEvent() re-run GET internally, so the calendar,
      // today's schedule, and the day bottom sheet are already
      // up to date — no reload, no extra fetch needed here.
    } catch (err) {
      // 通信エラー / Google APIエラー / タイムアウト — いずれもToastで通知し、
      // 入力内容を失わないようシートは閉じない。
      showToast(err?.message || '保存に失敗しました。もう一度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={closeEventForm}
      title={mode === 'edit' ? '予定を編集' : '予定を追加'}
      footer={
        <>
          <button className="btn btn-tonal" type="button" onClick={closeEventForm} disabled={submitting}>
            キャンセル
          </button>
          <button className="btn btn-filled" type="button" onClick={handleSubmit} disabled={submitting}>
            {submitting && <span className="btn-spinner" aria-hidden="true" />}
            {submitting ? '保存中…' : mode === 'edit' ? '保存' : '登録'}
          </button>
        </>
      }
    >
      <div className="field">
        <label>タイトル</label>
        <input
          type="text"
          placeholder="例）サッカー教室"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <DateInlineCalendar value={date} onChange={setDate} today={today} />

      <div className="switch-row" style={{ marginBottom: 6 }}>
        <div className="label-group">
          <div className="t">終日</div>
          <div className="s">時間を指定しない予定</div>
        </div>
        <div className="switch">
          <input
            type="checkbox"
            id="sw-allday"
            checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
          />
          <label className="track" htmlFor="sw-allday">
            <span className="thumb" />
          </label>
        </div>
      </div>

      <div className="field">
        <label>時間</label>
        <div className={`time-range-row${allDay ? ' disabled' : ''}`}>
          <TimeWheelPicker
            label="開始"
            hourIndex={startHourIdx}
            minuteIndex={startMinuteIdx}
            onChangeHour={setStartHourIdx}
            onChangeMinute={setStartMinuteIdx}
            disabled={allDay}
          />
          <div className="time-range-sep">〜</div>
          <TimeWheelPicker
            label="終了"
            hourIndex={endHourIdx}
            minuteIndex={endMinuteIdx}
            onChangeHour={setEndHourIdx}
            onChangeMinute={setEndMinuteIdx}
            disabled={allDay}
          />
        </div>
      </div>

      <div className="field">
        <label>通知先</label>
        <select value={notifyTarget} onChange={(e) => setNotifyTarget(e.target.value)}>
          {NOTIFY_TARGET_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label>テンプレートから選択</label>
        <TemplatePickerRow selectedId={templateId} onSelect={handleSelectTemplate} />
      </div>

      <div className="field">
        <label>場所</label>
        <input
          type="text"
          placeholder="例）〇〇スポーツ広場"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
        />
      </div>

      <div className="field">
        <label>説明</label>
        <textarea
          rows={2}
          placeholder={'例）\n・体育館8:30集合'}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="field" style={{ marginTop: 14, marginBottom: 4 }}>
        <label>予定色</label>
        <ColorRow value={color} onChange={setColor} />
      </div>
    </BottomSheet>
  );
}
